import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  OrganisationService,
  StateBasedNavigationService,
} from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import { Appointment, Practice } from '@principle-theorem/principle-core';
import {
  IAppointment,
  IFollowUp,
  MentionResourceType,
  PrincipleCreatedBy,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  filterUndefined,
  multiSwitchMap,
  snapshot,
  toNamedTypeDocument,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CancelFollowUpComponent } from './components/cancel-follow-up/cancel-follow-up.component';
import {
  FollowUpComponent,
  IAppointmentFollowUpData,
} from './components/follow-up/follow-up.component';
import {
  IPendingFollowUp,
  appointmentToPendingFollowUp$,
} from './pending-follow-up';

export enum FollowUpExportType {
  Overdue = 'overdue',
  Upcoming = 'upcoming',
  All = 'all',
}

@Injectable()
export class FollowUpsService {
  existingFollowUps$: Observable<IPendingFollowUp[]>;
  upcomingFollowUps$: Observable<IPendingFollowUp[]>;

  constructor(
    private _dialog: MatDialog,
    private _organisation: OrganisationService,
    private _snackBar: MatSnackBar,
    private _stateNav: StateBasedNavigationService
  ) {
    const practice$ = this._organisation.practice$.pipe(filterUndefined());

    this.upcomingFollowUps$ = practice$.pipe(
      switchMap((practice) =>
        Practice.getAppointmentsWithUpcomingFollowUps$(practice)
      ),
      multiSwitchMap((appointment) =>
        appointmentToPendingFollowUp$(appointment)
      )
    );

    this.existingFollowUps$ = practice$.pipe(
      switchMap((practice) =>
        Practice.getAppointmentsWithExistingFollowUps$(practice)
      ),
      multiSwitchMap((appointment) =>
        appointmentToPendingFollowUp$(appointment)
      )
    );
  }

  async reschedule(appointment: WithRef<IAppointment>): Promise<void> {
    const patient = await Appointment.patient(appointment);
    await this._stateNav.brand([
      'patients',
      patient.ref.id,
      'appointments',
      appointment.ref.id,
      'manage',
      'reschedule',
    ]);
  }

  async postpone(appointment: WithRef<IAppointment>): Promise<void> {
    const data = await this._getAppointmentFollowUpDate(appointment);
    this._dialog.open(FollowUpComponent, DialogPresets.medium({ data }));
  }

  async cancel(appointment: WithRef<IAppointment>): Promise<void> {
    const data = await this._getAppointmentFollowUpDate(appointment);
    this._dialog.open(CancelFollowUpComponent, DialogPresets.medium({ data }));
  }

  async addToAppointment(
    appointment: WithRef<IAppointment>,
    followUp: IFollowUp,
    message = 'Follow Up Set'
  ): Promise<void> {
    const owner = await this._getInteractionOwner();
    await Appointment.addFollowUp(appointment, followUp, owner);
    this._snackBar.open(message);
  }

  async getAllFollowUps$(
    type: FollowUpExportType.Overdue | FollowUpExportType.Upcoming
  ): Promise<Observable<IPendingFollowUp[]>> {
    const practice = await snapshot(
      this._organisation.practice$.pipe(filterUndefined())
    );
    const appointments$ =
      type === FollowUpExportType.Overdue
        ? Practice.getAppointmentsWithExistingFollowUps$(practice, 0)
        : Practice.getAppointmentsWithUpcomingFollowUps$(practice, 0);
    return appointments$.pipe(
      multiSwitchMap((appointment) =>
        appointmentToPendingFollowUp$(appointment)
      )
    );
  }

  private async _getAppointmentFollowUpDate(
    appointment: WithRef<IAppointment>
  ): Promise<IAppointmentFollowUpData> {
    return {
      appointment,
      patient: await Appointment.patient(appointment),
    };
  }

  private async _getInteractionOwner(): Promise<PrincipleCreatedBy> {
    const staffer = await snapshot(this._organisation.staffer$);
    if (!staffer) {
      throw new Error(`Couldn't resolve staffer`);
    }
    return toNamedTypeDocument(staffer, MentionResourceType.Staffer);
  }
}
